// PRC-019 · B1: bienvenida, solo la primera vez que entra el administrador.
import React from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { SimpleLayout } from '../components/JourneyLayouts';
import { Eyebrow, JourneyButton, JourneyCard, JourneyChip, JourneyText } from '../components/JourneyBasics';
import { formatCount, trialUsage } from '../components/TrialBanner';
import { J } from '../journeyTokens';
import { formatLongDate } from '../journeyBilling';
import { ONBOARDING_QUERY_KEY, PricingOnboarding, useMarkOnboardingEvent } from '../onboarding';
import { withReturnTo } from '../navigation';
import { useQueryClient } from '@tanstack/react-query';

const STEPS = [
  { n: 'PASO 1 · 5 MIN', title: 'Carga tu histórico de ventas', text: 'Un archivo CSV o Excel con tus ventas de los últimos 12 meses. Te decimos si falta algo antes de procesarlo.' },
  { n: 'PASO 2', title: 'Mira dónde se va el margen', text: 'Margin Center ordena clientes y productos por margen e ingresos y señala lo que merece una revisión.' },
  { n: 'PASO 3', title: 'Decide un precio con evidencia', text: 'Evalúa el precio de una combinación cliente-producto con su historial, su coste y su volumen.' },
];

const WelcomePage: React.FC = () => {
  const { user, pricingAccess, pricingAccountName, signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Vista una vez: no vuelve a salir. Se marca ya en la caché para no rebotar aquí.
  React.useEffect(() => {
    queryClient.setQueryData<PricingOnboarding>(ONBOARDING_QUERY_KEY, (old) => (old ? { ...old, welcome_seen: true } : old));
  }, [queryClient]);
  useMarkOnboardingEvent('welcome');

  const fullName = ((user as any)?.full_name as string | undefined) || '';
  const firstName = fullName.split(' ')[0];
  const company = pricingAccountName || 'tu empresa';
  const { used, limit } = trialUsage(pricingAccess);
  const isTrial = pricingAccess?.state === 'trial';
  const userLabel = [fullName || user?.email, pricingAccountName].filter(Boolean).join(' · ');

  return (
    <Box sx={{ backgroundImage: 'radial-gradient(ellipse at 50% -10%,rgba(56,189,248,.10),transparent 55%)', bgcolor: J.bg }}>
      <SimpleLayout userLabel={userLabel} onSignOut={() => { void signOut(); }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px', pt: { md: 1 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', maxWidth: 820 }}>
            <Eyebrow>Bienvenida</Eyebrow>
            <JourneyText variant="hero" sx={{ fontSize: { xs: 34, md: 48 } }}>
              {firstName ? `Hola, ${firstName}. ` : 'Hola. '}
              {isTrial ? 'Tu prueba de 30 días ha empezado.' : 'Tu cuenta está lista.'}
            </JourneyText>
            <JourneyText variant="lead" sx={{ fontSize: 18 }}>
              En unos minutos verás dónde gana y dónde pierde margen {company}. Solo necesitas tu histórico de ventas.
            </JourneyText>
            {isTrial && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {pricingAccess?.trial_ends_at && <JourneyChip tone="sky">Termina el {formatLongDate(pricingAccess.trial_ends_at)}</JourneyChip>}
                <JourneyChip>{formatCount(used)} de {formatCount(limit)} optimizaciones usadas</JourneyChip>
                <JourneyChip>Sin tarjeta · nada se cobra solo</JourneyChip>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: '16px' }}>
            {STEPS.map((s) => (
              <JourneyCard key={s.n} sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Box component="span" sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '.14em', color: J.amber }}>{s.n}</Box>
                <JourneyText variant="h2">{s.title}</JourneyText>
                <JourneyText variant="body">{s.text}</JourneyText>
              </JourneyCard>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px' }}>
            <JourneyButton sx={{ minWidth: 240 }} onClick={() => navigate(withReturnTo('/data-upload', '/bienvenida'))}>Cargar mis datos</JourneyButton>
            <JourneyButton kind="ghost" onClick={() => navigate('/margin-center/overview')}>Ver la aplicación primero</JourneyButton>
            <JourneyButton kind="quiet" onClick={() => navigate('/primeros-pasos')}>Ver la guía de inicio</JourneyButton>
          </Box>
          <JourneyCard tight sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: 720 }}>
              <JourneyText variant="h3">¿Tus datos necesitan limpieza o vienen de varios sistemas?</JourneyText>
              <JourneyText variant="small">Envíanos los archivos y los preparamos nosotros. Te avisamos cuando estén cargados.</JourneyText>
            </Box>
            <JourneyButton kind="ghost" small onClick={() => navigate(withReturnTo('/enviar-archivos', '/bienvenida'))}>Enviar archivos al equipo</JourneyButton>
          </JourneyCard>
        </Box>
      </SimpleLayout>
    </Box>
  );
};

export default WelcomePage;
